import { getLanguageFromLocalStorage } from 'applicaiton/sessionStorage/language';
import axiosInstance from './axiosInstance';
import {
  AdminCreationDto,
  AdminSetPasswordDto,
  AdminSetPasswordResDto,
  AdminUpdateDto,
} from '@docbay/schemas';
import axiosAuthInstance from './axiosAuthInstance';

/**
 * AdminsAPI module for handling CRUD operations on admins.
 */
export const AdminsAPI = {
  /**
   * Creates a new admin.
   * @param data - The admin creation data.
   * @returns A promise that resolves to the created admin.
   */
  createAdmin: (data: AdminCreationDto) => {
    const language = getLanguageFromLocalStorage();
    return axiosInstance.post('/admins', { ...data, language });
  },

  /**
   * Retrieves an admin by ID.
   * @param id - The admin's ID.
   * @returns A promise that resolves to the retrieved admin.
   */
  getAdminById: (id: string) => {
    const params = {
      include: 'photo,clinics,clinics.owner,clinicOwner',
    };

    return axiosInstance.get(`/admins/${id}`, { params });
  },

  /**
   * Edits an admin by ID.
   * @param id - The admin's ID.
   * @param data - The updated admin data.
   * @returns A promise that resolves to the updated admin.
   */
  editAdmin: (id: string, data: AdminUpdateDto) =>
    axiosInstance.patch(`/admins/${id}`, data),

  /**
   * Deletes an admin by ID.
   * @param id - The admin's ID.
   * @returns A promise that resolves when the admin is deleted.
   */
  deleteAdmin: (id: string) => axiosInstance.delete(`/admins/${id}`),

  /**
   * Checks an admin's verification code.
   * @param email - The admin's email address.
   * @param code - The verification code.
   * @returns A promise that resolves when the verification code is checked.
   */
  checkAdminVerificationCode: (email: string, code: string) =>
    axiosAuthInstance('').post('/admins/verification-code', { email, code }),

  /**
   * Creates an admin password.
   * @param data - The admin password creation data.
   * @returns A promise that resolves to the created password response.
   */
  createAdminPassword: (data: AdminSetPasswordDto) =>
    axiosAuthInstance('').post<AdminSetPasswordResDto>(
      '/admins/password',
      data,
    ),

  /**
   * Resends an admin's authorization data.
   * @param id - The admin's ID.
   * @returns A promise that resolves when the authorization data is resent.
   */
  resendAdminAuthorizationData: (id: string) => {
    const language = getLanguageFromLocalStorage();
    return axiosInstance.post(`/admins/${id}/resend-authorization-data`, {
      language,
    });
  },

  /**
   * Changes an admin's email address.
   * @param id - The admin's ID.
   * @param email - The new email address.
   * @param userType - The type of user making the change.
   * @returns A promise that resolves when the email address is changed.
   */
  changeAdminsEmail: (id: string, email: string, userType: string) => {
    const language = getLanguageFromLocalStorage();
    return axiosInstance.patch(`/admins/${id}/email`, {
      email,
      language,
      userType,
    });
  },
};
